import React, { useState, useMemo } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { DailyMovementReportRecord, DmrMovementReportRow, blankMovementReportRow } from '../core/_dmrModels'
import AddMovementReportModal from './AddMovementReportModal'
import { DailyPositionReportRecord } from '../core/_dprModels'

// Interfaces
interface MovementReportProps {
  selectedVessel: string
  reportDate: string
  dmrRecord: DailyMovementReportRecord
  setDmrRecord: React.Dispatch<React.SetStateAction<DailyMovementReportRecord>>
  dmrList: any[]
}

// Row with date attached for grouping
interface DatedMovementRow extends DmrMovementReportRow {
  _date: string
  _vesselName: string
}

const formatDateString = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

/** Normalize a date string to YYYY-MM-DD for comparison */
const normalizeDate = (dateStr: string): string => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  return d.toISOString().split('T')[0]
}

export const MovementReport: React.FC<MovementReportProps> = ({ selectedVessel, reportDate: pageReportDate, dmrRecord, setDmrRecord, dmrList }) => {

  const [isAddModalOpen, setIsAddModalOpen] = useState(false)

  /* ── filters ── */
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')

  /* ── filtering ── */
  const hasMovementData = (r: DmrMovementReportRow) => {
    return Boolean(
      (r.berShifting && r.berShifting !== '-') ||
      (r.pilotName && r.pilotName !== '-') ||
      (r.startedBothME && r.startedBothME !== '-') ||
      (r.cOff && r.cOff !== '-') ||
      (r.makeFastPushStart && r.makeFastPushStart !== '-') ||
      (r.lineCastOffPushStop && r.lineCastOffPushStop !== '-') ||
      (r.pilotRelease && r.pilotRelease !== '-') ||
      (r.alongSideFinishedME && r.alongSideFinishedME !== '-') ||
      (r.otherTug && r.otherTug !== '-') ||
      (r.remarks && r.remarks !== '-')
    )
  }

  /**
   * Combine current session rows + historical dmrList rows,
   * then filter by date range and group by date.
   */
  const allDatedRows = useMemo(() => {
    const rows: DatedMovementRow[] = []

    // 1. Historical rows from submitted DMRs (older entries first)
    if (dmrList && dmrList.length > 0) {
      // Sort DMRs by date ascending so older reports come first
      const sortedDmrs = [...dmrList].sort((a, b) => {
        return normalizeDate(a.reportDate).localeCompare(normalizeDate(b.reportDate))
      })
      for (const dmr of sortedDmrs) {
        const dmrDate = normalizeDate(dmr.reportDate)
        const dmrVessel = dmr.vesselName || selectedVessel || ''
        const dmrRows: DmrMovementReportRow[] = dmr.movementReportRows || []
        dmrRows.forEach(r => {
          if (hasMovementData(r)) {
            rows.push({ ...r, _date: dmrDate, _vesselName: dmrVessel })
          }
        })
      }
    }

    // 2. Current session rows (not yet submitted) — appear at the bottom
    const currentRows = dmrRecord.movementReportRows || []
    const currentDate = normalizeDate(pageReportDate || new Date().toISOString().split('T')[0])
    currentRows.forEach(r => {
      if (hasMovementData(r)) {
        rows.push({ ...r, _date: currentDate, _vesselName: selectedVessel || dmrRecord.vesselName || '' })
      }
    })

    return rows
  }, [dmrRecord.movementReportRows, dmrList, pageReportDate, selectedVessel])

  /** Apply date range filter */
  const filteredRows = useMemo(() => {
    return allDatedRows.filter(r => {
      if (fromDate && r._date < fromDate) return false
      if (toDate && r._date > toDate) return false
      return true
    })
  }, [allDatedRows, fromDate, toDate])

  /** Group filtered rows by date, sorted ascending */
  const groupedReports = useMemo(() => {
    const groupMap = new Map<string, DatedMovementRow[]>()

    for (const row of filteredRows) {
      const existing = groupMap.get(row._date) || []
      existing.push(row)
      groupMap.set(row._date, existing)
    }

    // Sort by date ascending
    const sorted = Array.from(groupMap.entries()).sort((a, b) => a[0].localeCompare(b[0]))

    return sorted.map(([date, rows]) => ({
      date,
      formattedDate: formatDateString(date),
      rows,
    }))
  }, [filteredRows])

  /** Total running hours across all visible rows */
  const totalRunningHours = useMemo(() => {
    const sum = filteredRows.reduce((acc, r) => {
      const val = parseFloat(r.totalRHrs || '0')
      return acc + (isNaN(val) ? 0 : val)
    }, 0)
    return sum.toFixed(2)
  }, [filteredRows])

  const handleSaveMovementReport = async (payload: DailyPositionReportRecord) => {
    const newRow: DmrMovementReportRow = payload.movementReport || blankMovementReportRow()
    setDmrRecord(prev => ({
      ...prev,
      movementReportRows: [...prev.movementReportRows, newRow]
    }))
    setIsAddModalOpen(false)
  }

  const renderCellData = (val: string) => {
    if (val?.toLowerCase() === 'continue') {
      return <span className='badge badge-light-danger fs-8 fw-bolder'>Continue</span>
    }
    return val || '-'
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 d-flex justify-content-between align-items-center pt-5 pb-4'>
        <div className='card-title m-0'>
          <h3 className='fw-bold m-0'>Movement Report</h3>
        </div>
        <div className='d-flex align-items-center gap-4 flex-wrap'>
          <div className='d-flex align-items-center gap-2'>
            <label className='form-label mb-0 fw-bolder text-muted fs-7 text-uppercase' style={{ whiteSpace: 'nowrap' }}>Vessel:</label>
            <span className='fw-bolder text-dark me-3'>{selectedVessel || 'Select vessel in Vessel Status'}</span>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <label className='form-label mb-0 fw-bolder text-muted fs-7 text-uppercase' style={{ whiteSpace: 'nowrap' }}>From:</label>
            <input 
              type='date' 
              className='form-control form-control-sm form-control-solid' 
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              style={{ minWidth: '130px' }}
            />
          </div>
          <div className='d-flex align-items-center gap-2'>
            <label className='form-label mb-0 fw-bolder text-muted fs-7 text-uppercase' style={{ whiteSpace: 'nowrap' }}>To:</label>
            <input 
              type='date' 
              className='form-control form-control-sm form-control-solid' 
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              style={{ minWidth: '130px' }}
            />
          </div>
          {(fromDate || toDate) && (
            <button
              type='button'
              className='btn btn-sm btn-light-danger fw-bolder'
              onClick={() => { setFromDate(''); setToDate('') }}
              title='Clear date filters'
            >
              Clear
            </button>
          )}
          <button 
            type='button' 
            className='btn btn-sm btn-primary fw-bolder d-flex align-items-center gap-2 shadow-sm'
            onClick={() => setIsAddModalOpen(true)}
          >
            <KTSVG path='/media/icons/duotune/general/gen035.svg' className='svg-icon-3 m-0' />
            Add Movement Report
          </button>
        </div>
      </div>
      <div className='card-body py-3'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-4 gy-3 mb-0' style={{ fontSize: '12px' }}>
            <thead>
              <tr className='fw-bolder text-muted text-center bg-light'>
                <th className='min-w-80px rounded-start'>DATE</th>
                <th className='min-w-100px'>VESSEL NAME</th>
                <th className='min-w-100px'>BER/U.BER/SHIFTING</th>
                <th className='min-w-70px'>PILOT NAME</th>
                <th className='min-w-80px'>STARTED BOTH M.E.</th>
                <th className='min-w-60px'>C/OFF</th>
                <th className='min-w-90px'>MAKE FAST /PUSH START</th>
                <th className='min-w-100px'>LINE CAST OFF/PUSH STOP</th>
                <th className='min-w-80px'>PILOT RELEASE</th>
                <th className='min-w-110px'>ALONG SIDE/ FINISHED M.E.</th>
                <th className='min-w-60px'>TOTAL R/HRS</th>
                <th className='min-w-100px'>OTHER TUG</th>
                <th className='min-w-100px rounded-end'>REMARKS</th>
              </tr>
            </thead>
            <tbody className='text-center text-gray-800 fw-bold'>
              {groupedReports.length === 0 ? (
                <tr>
                  <td colSpan={13} className='text-center py-5 text-muted'>
                    {fromDate || toDate
                      ? 'No Movement Reports found for the selected date range.'
                      : 'No Movement Reports added yet. Click "Add Movement Report".'}
                  </td>
                </tr>
              ) : (
                groupedReports.map((group, groupIndex) => {
                  return group.rows.map((row, rowIndex) => (
                    <tr key={`${group.date}-${groupIndex}-${rowIndex}`} className='hover-elevate-up'>
                      {rowIndex === 0 && (
                        <td 
                          rowSpan={group.rows.length} 
                          className='align-middle border-end border-gray-300 bg-light-primary text-primary fs-6'
                        >
                          {group.formattedDate}
                        </td>
                      )}
                      <td className='text-dark fw-bolder'>{row._vesselName || selectedVessel || '-'}</td>
                      <td>{renderCellData(row.berShifting)}</td>
                      <td>{renderCellData(row.pilotName)}</td>
                      <td>{renderCellData(row.startedBothME)}</td>
                      <td>{renderCellData(row.cOff)}</td>
                      <td>{renderCellData(row.makeFastPushStart)}</td>
                      <td>{renderCellData(row.lineCastOffPushStop)}</td>
                      <td>{renderCellData(row.pilotRelease)}</td>
                      <td>{renderCellData(row.alongSideFinishedME)}</td>
                      <td className='text-dark fw-bolder'>{row.totalRHrs || '-'}</td>
                      <td>{renderCellData(row.otherTug)}</td>
                      <td className='text-start text-muted fw-normal'>{renderCellData(row.remarks)}</td>
                    </tr>
                  ))
                })
              )}
            </tbody>
            {filteredRows.length > 0 && (
              <tfoot>
                <tr className='fw-bolder bg-light text-dark'>
                  <td colSpan={10} className='text-end pe-5 fs-5'>Total Hours</td>
                  <td className='text-center fs-5 text-primary'>{totalRunningHours}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <AddMovementReportModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveMovementReport}
        selectedVessel={selectedVessel}
        reportDate={pageReportDate}
        existingRows={dmrRecord.movementReportRows || []}
        dmrList={dmrList || []}
      />
    </div>
  )
}

export default MovementReport

